import React, { memo } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Typography } from '@mui/material'
import {
  ContractorStepDetailsCardUsageVariant,
  DECLINED_STATUS_LABEL,
  METADATA_LABEL_COLOR,
  statusLabels,
  masterContractorStatusLabels,
} from '../constants'
import { ContractorStepDetailsDeclineReasonBlock } from './ContractorStepDetailsDeclineReasonBlock'
import { ContractorStepDetailsStatusLabel } from './ContractorStepDetailsStatusLabel'
import { ContractorStepDetailsCardActions } from './ContractorStepDetailsCardActions'
import { SubmissionBlock } from '../types'
import { isApprovalQueueCardStatus } from '../utils'
import { isAwaitingInternalApprovalAfterCustomer } from 'src/utils/collectedData'

dayjs.extend(utc)

interface ContractorStepDetailsBaseContentBlockProps {
  block: SubmissionBlock
  usageVariant: ContractorStepDetailsCardUsageVariant
  readOnly?: boolean
}

const ContractorStepDetailsBaseContentBlockComponent = ({
  block,
  usageVariant,
  readOnly = false,
}: ContractorStepDetailsBaseContentBlockProps) => {
  const showReviewButtonWithText = block.showReviewButtonWithText ?? false

  const baseStatusLabel =
    usageVariant === ContractorStepDetailsCardUsageVariant.MasterContractor
      ? masterContractorStatusLabels[block.status]
      : statusLabels[block.status]
  const declineReason = block.collectedData?.lastRejectionReason
  const isDeclinedStep = !!(
    declineReason &&
    !isApprovalQueueCardStatus(block.status) &&
    !isAwaitingInternalApprovalAfterCustomer(block.collectedData)
  )
  const statusLabel = isDeclinedStep ? DECLINED_STATUS_LABEL : baseStatusLabel
  const effectiveAtFormatted =
    block.effectiveAt ??
    (block.collectedData?.effectiveAt
      ? dayjs(block.collectedData.effectiveAt).utc().format('MM/DD/YYYY')
      : undefined)

  return (
    <div css={tw`px-4`}>
      <div css={[tw`flex flex-row items-start gap-x-4 min-w-0 w-full py-4`]}>
        <div css={[tw`flex flex-col shrink-0`, { minWidth: 80 }]}>
          <Typography variant="body2" fontWeight={400} color={METADATA_LABEL_COLOR}>
            Submission
          </Typography>
          {statusLabel != null && (
            <ContractorStepDetailsStatusLabel
              label={statusLabel}
              status={block.status}
              isDeclined={isDeclinedStep}
            />
          )}
        </div>
        {block.showEffectiveDate && (
          <div css={tw`flex flex-col min-w-0 shrink`}>
            <Typography variant="body2" fontWeight={400} color={METADATA_LABEL_COLOR}>
              Effective
            </Typography>
            <Typography variant="body2" color="textPrimary">
              {effectiveAtFormatted || 'Not Set'}
            </Typography>
          </div>
        )}
        {block.showExpirationDate && (
          <div css={tw`flex flex-col min-w-0 shrink`}>
            <Typography variant="body2" fontWeight={400} color={METADATA_LABEL_COLOR}>
              Expiration
            </Typography>
            <Typography variant="body2" color="textPrimary">
              {block.expiresAt || 'Not Set'}
            </Typography>
          </div>
        )}
        <div css={tw`flex-1 min-w-0 shrink`} />
        {!readOnly && (
          <div css={tw`ml-2 shrink-0 flex items-center`}>
            <ContractorStepDetailsCardActions
              actions={block.actions}
              collectedData={block.collectedData}
              onReviewStepDataAction={block.onReviewStepDataAction}
              showReviewButtonWithText={showReviewButtonWithText}
            />
          </div>
        )}
      </div>
      {isDeclinedStep && declineReason && (
        <div css={tw`-mx-4 mb-0`}>
          <ContractorStepDetailsDeclineReasonBlock declineReason={declineReason} />
        </div>
      )}
    </div>
  )
}

export const ContractorStepDetailsBaseContentBlock = memo(
  ContractorStepDetailsBaseContentBlockComponent,
)
